import React, { useState, useEffect, useCallback } from "react";
import { apiCall, useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Phone, MessageSquare, Search, Flame, UserPlus, Users, UserCheck, Trash2, Edit3, Copy, Settings, RotateCcw } from "lucide-react";
import EditClientModal from "@/components/EditClientModal";

const SOURCE_LABELS = {
  all: "All Sources",
  google_sheet: "Google Sheet",
  extension: "Extension",
  csv_upload: "CSV Upload",
};

const DEFAULT_SOURCES = [
  "google_sheet",
  "extension",
  "csv_upload",
];

export const getSourceLabel = (src) => {
  if (!src || src === "all") return "All Sources";
  if (src === "csv") return "CSV Upload";
  if (src === "api") return "Google Sheet";
  if (SOURCE_LABELS[src]) return SOURCE_LABELS[src];
  return src.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

export default function ClientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState(user?.role === "agent" ? "leads" : "all");
  const [search, setSearch] = useState("");
  const [callModal, setCallModal] = useState(null);
  const [callResponse, setCallResponse] = useState({ response: "", notes: "" });
  const [calling, setCalling] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [assignAgent, setAssignAgent] = useState("");
  const [showAssign, setShowAssign] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [statusChangeModal, setStatusChangeModal] = useState(null);
  const [editClientModal, setEditClientModal] = useState(null);
  const [manualStatus, setManualStatus] = useState({ status: "", notes: "" });
  const [whatsappModal, setWhatsappModal] = useState(null);
  const [whatsappMsg, setWhatsappMsg] = useState("");
  const [waTemplates, setWaTemplates] = useState([]);
  const [agentFilter, setAgentFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [availableSites, setAvailableSites] = useState([]);
  const [availableSources, setAvailableSources] = useState(DEFAULT_SOURCES);
  const [availableBranches, setAvailableBranches] = useState([]);

  // Date filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Delayed reports state
  const [delayedReports, setDelayedReports] = useState([]);

  // Remark Modal State for Delayed Reports
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [selectedReportForRemark, setSelectedReportForRemark] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [savingRemark, setSavingRemark] = useState(false);

  const getLocalDateString = (offsetDays = 0) => {
    const d = new Date();
    d.setDate(d.getDate() - offsetDays);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleSetToday = () => {
    const today = getLocalDateString(0);
    setDateFrom(today);
    setDateTo(today);
  };

  const handleSetYesterday = () => {
    const yesterday = getLocalDateString(1);
    setDateFrom(yesterday);
    setDateTo(yesterday);
  };

  const hasActiveFilters = category !== "all" || (statusFilter !== "all" && statusFilter !== (user?.role === "agent" ? "leads" : "all")) || !!search || agentFilter !== "all" || sourceFilter !== "all" || branchFilter !== "all" || siteFilter !== "all" || !!dateFrom || !!dateTo;

  const handleResetFilters = () => {
    setCategory("all");
    setStatusFilter(user?.role === "agent" ? "leads" : "all");
    setSearch("");
    setAgentFilter("all");
    setSourceFilter("all");
    setBranchFilter("all");
    setSiteFilter("all");
    setDateFrom("");
    setDateTo("");
  };


  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 10000 });
      if (category !== "all") params.set("category", category);
      if (statusFilter === "leads") params.set("leads_only", "true");
      else if (statusFilter && !["all", "unassigned", "assigned"].includes(statusFilter)) params.set("status", statusFilter);
      if (statusFilter === "unassigned") params.set("assigned", "unassigned");
      if (statusFilter === "assigned") params.set("assigned", "assigned");
      if (search) params.set("search", search);
      if (agentFilter !== "all") params.set("agent_filter", agentFilter);
      if (siteFilter !== "all") params.set("site", siteFilter);
      if (sourceFilter !== "all") params.set("source", sourceFilter);
      if (branchFilter !== "all") params.set("branch", branchFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      
      const { data } = await apiCall("get", `/clients?${params}`);
      setClients(data.clients);
      setTotal(data.total);
      setDelayedReports([]);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, [category, statusFilter, search, agentFilter, siteFilter, sourceFilter, branchFilter, dateFrom, dateTo]);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => {
    if (user?.role === "admin") {
      apiCall("get", "/agents").then(({ data }) => setAgents(data)).catch(() => {});
      apiCall("get", "/clients/filters").then(({ data }) => {
        setAvailableSites(data.sites || []);
        setAvailableSources(Array.from(new Set([...DEFAULT_SOURCES, ...(data.sources || [])])));
        setAvailableBranches(data.branches || []);
      }).catch(() => {});
    }
  }, [user]);
  useEffect(() => { apiCall("get", "/whatsapp/templates").then(({ data }) => setWaTemplates(data.templates || [])).catch(() => {}); }, []);

  const handleOpenRemarkModal = (report, reportType) => {
    setSelectedReportForRemark({ ...report, report_type: reportType });
    setRemarkText(report.remark || "");
    setRemarkModalOpen(true);
  };

  const handleSaveRemark = async () => {
    if (!selectedReportForRemark) return;
    setSavingRemark(true);
    try {
      await apiCall("post", "/reports/update-remark", {
        report_type: selectedReportForRemark.report_type,
        user_id: selectedReportForRemark.user_id || "",
        utr: selectedReportForRemark.utr || "",
        request_time: selectedReportForRemark.request_time || "",
        remark: remarkText
      });
      toast.success("Reason for delay saved!");
      setRemarkModalOpen(false);
      fetchClients();
    } catch (err) {
      toast.error("Failed to save remark");
    } finally {
      setSavingRemark(false);
    }
  };

  const handleDeleteReport = async (report, reportType) => {
    if (!window.confirm("Are you sure you want to delete this delayed report?")) return;
    try {
      await apiCall("post", "/reports/delete", {
        report_type: reportType,
        user_id: report.user_id || "",
        utr: report.utr || "",
        request_time: report.request_time || ""
      });
      toast.success("Delayed report deleted successfully!");
      fetchClients();
    } catch (err) {
      toast.error("Failed to delete delayed report");
    }
  };

  const toggleSelect = (id) => setSelectedClients(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const copyId = (id) => { navigator.clipboard.writeText(`${id}`); toast.success(`${id} copied`); };

  const handleAssign = async () => {
    if (!assignAgent || !selectedClients.length) { toast.error("Select agent and clients"); return; }
    try { const { data } = await apiCall("post", "/clients/assign", { agent_id: assignAgent, client_ids: selectedClients }); toast.success(`${data.assigned} assigned`); setSelectedClients([]); setShowAssign(false); fetchClients(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const handleAutoAssign = async () => { try { const { data } = await apiCall("post", "/clients/auto-assign"); toast.success(`${data.assigned} auto-assigned`); fetchClients(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const handleDelete = async () => {
    if (!deletePassword) { toast.error("Enter password"); return; }
    try { const { data } = await apiCall("post", "/clients/delete", { client_ids: selectedClients, password: deletePassword }); toast.success(`${data.deleted} deleted`); setSelectedClients([]); setDeleteModal(false); setDeletePassword(""); fetchClients(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleCall = async (client) => {
    setCalling(true);
    try {
      const { data } = await apiCall("post", "/bonvoice/click2call", { client_user_id: client.user_id, destination: client.mobile_masked ? "MASKED" : client.mobile_no });
      toast.success("Call initiated!");
      setCallModal({ ...client, event_id: data.event_id });
      setCallResponse({ response: "", notes: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Call failed"); }
    finally { setCalling(false); }
  };

  const submitResponse = async () => {
    if (!callResponse.response) { toast.error("Select response"); return; }
    try { await apiCall("post", "/call-response", { client_id: callModal.user_id, call_id: callModal.event_id || "", response: callResponse.response, notes: callResponse.notes }); toast.success("Saved"); setCallModal(null); setCallResponse({ response: "", notes: "" }); fetchClients(); } catch { toast.error("Failed"); }
  };

  const submitManualStatus = async () => {
    if (!manualStatus.status) { toast.error("Select status"); return; }
    try { await apiCall("post", "/clients/update-status", { client_id: statusChangeModal.user_id, status: manualStatus.status, notes: manualStatus.notes }); toast.success("Updated"); setStatusChangeModal(null); setManualStatus({ status: "", notes: "" }); fetchClients(); } catch { toast.error("Failed"); }
  };

  const handleWhatsApp = (client) => { setWhatsappModal(client); setWhatsappMsg(waTemplates.length ? waTemplates[0].body : "Hello!"); };
  const sendWhatsApp = async () => { try { await apiCall("post", "/whatsapp/send-wati", { client_user_id: whatsappModal.user_id, message: whatsappMsg }); toast.success("Sent!"); setWhatsappModal(null); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };

  const getCatBadge = (cat) => {
    if (cat === "new") return <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 text-[10px]"><UserPlus className="w-3 h-3 mr-0.5" />New</Badge>;
    if (cat === "hot") return <Badge className="bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20 text-[10px]"><Flame className="w-3 h-3 mr-0.5" />Hot</Badge>;
    return <Badge variant="secondary" className="text-[10px]">Regular</Badge>;
  };
  const getResBadge = (r) => {
    if (!r) return <span className="text-xs text-muted-foreground">-</span>;
    const m = { interested: "bg-green-500/10 text-green-600", not_interested: "bg-red-500/10 text-red-600", follow_up: "bg-yellow-500/10 text-yellow-600", not_connected: "bg-gray-500/10 text-gray-600", converted: "bg-purple-500/10 text-purple-600" };
    return <Badge className={`text-[10px] capitalize ${m[r] || ""}`}>{r.replace(/_/g, " ")}</Badge>;
  };
  const whatsappVisible = user?.role === "admin" || user?.whatsapp_enabled;

  return (
    <div className="space-y-4" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{user?.role === "admin" ? "All Clients" : "My Clients"}</h2>
          <p className="text-sm text-muted-foreground">{total} clients</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.role === "admin" && selectedClients.length > 0 && (
            <>
              <Button size="sm" onClick={() => setShowAssign(true)}><UserCheck className="w-3 h-3 mr-1" /> Assign ({selectedClients.length})</Button>
              <Button size="sm" variant="destructive" onClick={() => setDeleteModal(true)}><Trash2 className="w-3 h-3 mr-1" /> Del</Button>
            </>
          )}
          {user?.role === "admin" && <Button variant="outline" size="sm" onClick={() => setSelectedClients(prev => prev.length === clients.length ? [] : clients.map(c => c.user_id))}>Select All</Button>}
          {user?.role === "admin" && <Button variant="outline" size="sm" onClick={handleAutoAssign}>Auto Assign</Button>}
          {user?.role === "admin" && (
            <Button variant="outline" size="sm" onClick={() => {
              const token = localStorage.getItem("token");
              const params = new URLSearchParams();
              if (statusFilter && !["all","unassigned","assigned","leads"].includes(statusFilter)) params.set("status", statusFilter);
              if (category !== "all") params.set("category", category);
              if (statusFilter === "unassigned") params.set("assigned", "unassigned");
              if (statusFilter === "assigned") params.set("assigned", "assigned");
              if (search) params.set("search", search);
              if (agentFilter !== "all") params.set("agent_filter", agentFilter);
              if (siteFilter !== "all") params.set("site", siteFilter);
              if (sourceFilter !== "all") params.set("source", sourceFilter);
              if (branchFilter !== "all") params.set("branch", branchFilter);
              if (dateFrom) params.set("date_from", dateFrom);
              if (dateTo) params.set("date_to", dateTo);
              fetch(`${process.env.REACT_APP_BACKEND_URL}/api/download/clients-filtered?${params}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.blob()).then(b => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `clients.csv`; a.click(); });
            }}>CSV</Button>
          )}
          {user?.role === "admin" && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={async () => {
                const token = localStorage.getItem("token");
                const loadingToast = toast.loading("Exporting to Google Sheets...");
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sheets/export`, {
                    method: "POST",
                    headers: { 
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}` 
                    },
                    body: JSON.stringify({}),
                    credentials: "include"
                  });
                  const data = await response.json();
                  toast.dismiss(loadingToast);
                  if (!response.ok) throw new Error(data.detail || "Export failed");
                  toast.success(data.message || "Exported successfully to Google Sheets!");
                } catch (err) {
                  toast.dismiss(loadingToast);
                  toast.error(err.message || "Failed to export to Google Sheets");
                }
              }}
            >
              Sheets Export
            </Button>
          )}
          <div className="flex items-center gap-1.5 border border-border rounded px-2 bg-card h-8">
            <span className="text-[10px] text-muted-foreground uppercase font-semibold">From:</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-transparent border-0 outline-none text-xs w-28 p-0" />
          </div>
          <div className="flex items-center gap-1.5 border border-border rounded px-2 bg-card h-8">
            <span className="text-[10px] text-muted-foreground uppercase font-semibold">To:</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-transparent border-0 outline-none text-xs w-28 p-0" />
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs px-2.5" onClick={handleSetToday}>Today</Button>
          <Button variant="outline" size="sm" className="h-8 text-xs px-2.5" onClick={handleSetYesterday}>Yesterday</Button>
          <div className="relative"><Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" /><Input placeholder="User ID..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 w-40 h-8 text-sm" /></div>
          <Select value={category} onValueChange={setCategory}><SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Cat.</SelectItem><SelectItem value="fresh">Fresh</SelectItem><SelectItem value="hot">Hot</SelectItem><SelectItem value="regular">Regular</SelectItem><SelectItem value="cold">Cold</SelectItem></SelectContent></Select>
          {user?.role === "admin" && <Select value={agentFilter} onValueChange={setAgentFilter}><SelectTrigger className="w-28 h-8 text-xs"><SelectValue placeholder="Agent" /></SelectTrigger><SelectContent><SelectItem value="all">All Agents</SelectItem>{agents.map(a => <SelectItem key={a._id} value={a._id}>{a.name}</SelectItem>)}</SelectContent></Select>}
          {user?.role === "admin" && (
            <Select value={sourceFilter || "all"} onValueChange={v => setSourceFilter(v)}>
              <SelectTrigger className="w-36 h-8 text-xs">
                <SelectValue placeholder="Select Source">
                  {getSourceLabel(sourceFilter)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {availableSources.map(src => (
                  <SelectItem key={src} value={src}>{getSourceLabel(src)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {user?.role === "admin" && (
            <Select value={branchFilter || "all"} onValueChange={v => setBranchFilter(v)}>
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue placeholder="Select Branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {availableBranches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {user?.role === "admin" && (
            <Select value={siteFilter || "all"} onValueChange={v => setSiteFilter(v)}>
              <SelectTrigger className="w-32 h-8 text-xs">
                <SelectValue placeholder="Select Site" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {availableSites.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs px-2.5 text-muted-foreground hover:text-foreground flex items-center gap-1"
              onClick={handleResetFilters}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto pb-1">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="inline-flex w-auto">
            <TabsTrigger value="leads" className="text-xs">Leads to Call</TabsTrigger>
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            <TabsTrigger value="converted" className="text-xs">Converted</TabsTrigger>
            <TabsTrigger value="interested" className="text-xs">Interested</TabsTrigger>
            <TabsTrigger value="not_interested" className="text-xs">Not Int.</TabsTrigger>
            <TabsTrigger value="follow_up" className="text-xs">Follow Up</TabsTrigger>
            <TabsTrigger value="not_connected" className="text-xs">Not Conn.</TabsTrigger>
            {user?.role === "admin" && <TabsTrigger value="unassigned" className="text-xs">Unassigned</TabsTrigger>}
            {user?.role === "admin" && <TabsTrigger value="assigned" className="text-xs">Assigned</TabsTrigger>}

          </TabsList>
        </Tabs>
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          <div className="max-h-[calc(100vh-16rem)] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow className="hover:bg-transparent">
                  {user?.role === "admin" && <TableHead className="w-8"></TableHead>}
                  {user?.role === "admin" && <TableHead className="text-[10px] uppercase">Mobile</TableHead>}
                  <TableHead className="text-[10px] uppercase">User ID</TableHead>
                  <TableHead className="text-[10px] uppercase">Username</TableHead>
                  <TableHead className="text-[10px] uppercase">Total Dep.</TableHead>
                  <TableHead className="text-[10px] uppercase">Last Dep.</TableHead>
                  <TableHead className="text-[10px] uppercase">Total Wdr.</TableHead>
                  <TableHead className="text-[10px] uppercase">Last Wdr.</TableHead>
                  <TableHead className="text-[10px] uppercase">Branch</TableHead>
                  <TableHead className="text-[10px] uppercase">Site Name</TableHead>
                  <TableHead className="text-[10px] uppercase">Source</TableHead>
                  <TableHead className="text-[10px] uppercase">Reg. Date</TableHead>
                  <TableHead className="text-[10px] uppercase">Status</TableHead>
                  {user?.role === "admin" && <TableHead className="text-[10px] uppercase">Agent</TableHead>}
                  <TableHead className="text-[10px] uppercase">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={14} className="text-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" /></TableCell></TableRow>
                ) : clients.length === 0 ? (
                  <TableRow><TableCell colSpan={14} className="text-center py-8 text-muted-foreground">No clients</TableCell></TableRow>
                ) : clients.map((c, i) => (
                  <TableRow key={i} className={`hover:bg-muted/50 ${selectedClients.includes(c.user_id) ? "bg-primary/5" : ""}`}>
                    {user?.role === "admin" && <TableCell onClick={e => e.stopPropagation()}><Checkbox checked={selectedClients.includes(c.user_id)} onCheckedChange={() => toggleSelect(c.user_id)} /></TableCell>}
                    {user?.role === "admin" && <TableCell className="text-xs font-mono">{c.mobile_no}</TableCell>}
                    <TableCell>
                      <button className="font-medium text-xs text-primary hover:underline flex items-center gap-1" onClick={() => copyId(c.user_id)} title="Copy">
                        {c.user_id} <Copy className="w-2.5 h-2.5 opacity-50" />
                      </button>
                      {c.delayed_type && (
                        <div className="mt-1 text-[9px] leading-tight">
                          <Badge variant="outline" className={`text-[8px] px-1 py-0 ${c.delayed_type === 'deposit' ? 'border-emerald-500 text-emerald-600 bg-emerald-500/5' : 'border-rose-500 text-rose-600 bg-rose-500/5'}`}>
                            Delayed {c.delayed_type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                          </Badge>
                          <div className="text-muted-foreground mt-0.5 font-medium">
                            {c.delayed_approve_delay_in_mins} mins delay
                          </div>
                          {c.delayed_remark && (
                            <div className="text-amber-600 max-w-[120px] truncate" title={c.delayed_remark}>
                              {c.delayed_remark}
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{c.username || "-"}</TableCell>
                    <TableCell className="text-xs font-medium text-green-600">{c.total_deposit ? `₹${Number(c.total_deposit).toLocaleString()}` : 0}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{c.last_deposit ? c.last_deposit.split(" ")[0] : "-"}</TableCell>
                    <TableCell className="text-xs text-red-500">{c.total_withdraw ? `₹${Number(c.total_withdraw).toLocaleString()}` : 0}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{c.last_withdraw ? c.last_withdraw.split(" ")[0] : "-"}</TableCell>
                    <TableCell className="text-[10px]">{c.branch || "-"}</TableCell>
                    <TableCell className="text-[10px]">{c.site || "-"}</TableCell>
                    <TableCell className="text-[10px] capitalize">{getSourceLabel(c.source)}</TableCell>
                    <TableCell className="text-xs">{c.registration_date || "-"}</TableCell>
                    <TableCell>{getResBadge(c.last_call_response)}</TableCell>
                    {user?.role === "admin" && <TableCell className="text-xs">{c.assigned_agent_name ? <Badge variant="outline" className="text-[10px]">{c.assigned_agent_name}</Badge> : <span className="text-orange-500 text-[10px]">-</span>}</TableCell>}
                    <TableCell>
                      <div className="flex gap-0.5">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Call Client" onClick={() => handleCall(c)} disabled={calling}><Phone className="w-3 h-3 text-green-500" /></Button>
                        {whatsappVisible && <Button variant="ghost" size="icon" className="h-7 w-7" title="WhatsApp" onClick={() => handleWhatsApp(c)}><MessageSquare className="w-3 h-3 text-green-600" /></Button>}
                        {user?.role === "admin" ? (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit All Client Details" onClick={() => setEditClientModal(c)}><Edit3 className="w-3 h-3 text-blue-500" /></Button>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Update Status" onClick={() => { setStatusChangeModal(c); setManualStatus({ status: c.last_call_response || "", notes: "" }); }}><Edit3 className="w-3 h-3 text-blue-500" /></Button>
                        )}
                        {user?.role === "admin" && c.delayed_type && (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit Reason for Delay" onClick={() => handleOpenRemarkModal({ user_id: c.user_id, utr: c.delayed_utr, request_time: c.delayed_request_time, remark: c.delayed_remark }, c.delayed_type)}><Settings className="w-3 h-3 text-amber-500" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Delete Delayed Report" onClick={() => handleDeleteReport({ user_id: c.user_id, utr: c.delayed_utr, request_time: c.delayed_request_time }, c.delayed_type)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                          </>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="View Details" onClick={() => navigate(`/clients/${c.user_id}`)}><Search className="w-3 h-3" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Remark / Reason for Delay Modal */}
      <Dialog open={remarkModalOpen} onOpenChange={setRemarkModalOpen}>
        <DialogContent className="sm:max-w-md max-w-[95vw]">
          <DialogHeader>
            <DialogTitle>Reason for Delay - {selectedReportForRemark?.user_id}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">UTR: {selectedReportForRemark?.utr || "-"}</p>
            <Textarea
              placeholder="Reason for delay / remark..."
              value={remarkText}
              onChange={(e) => setRemarkText(e.target.value)}
              rows={3}
              className="text-sm"
            />
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" size="sm" onClick={() => setRemarkModalOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveRemark} disabled={savingRemark}>
              {savingRemark ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Call Response Modal */}
      <Dialog open={!!callModal} onOpenChange={() => setCallModal(null)}>
        <DialogContent className="sm:max-w-md max-w-[95vw]"><DialogHeader><DialogTitle className="text-base">Call Response - {callModal?.user_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-2 rounded bg-green-500/5 border border-green-500/20"><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /><p className="text-xs font-medium">Call in progress...</p></div></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {["interested", "not_interested", "follow_up", "not_connected"].map(r => (
                <Button key={r} variant={callResponse.response === r ? "default" : "outline"} size="sm" onClick={() => setCallResponse({ ...callResponse, response: r })} className="capitalize text-[10px] h-8 px-2">{r.replace(/_/g, " ")}</Button>
              ))}
            </div>
            <Textarea placeholder="Remarks..." value={callResponse.notes} onChange={e => setCallResponse({ ...callResponse, notes: e.target.value })} rows={2} className="text-sm" />
          </div>
          <DialogFooter className="flex-row gap-2"><Button variant="outline" size="sm" onClick={() => setCallModal(null)}>Close</Button><Button size="sm" onClick={submitResponse}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Status Change */}
      <Dialog open={!!statusChangeModal} onOpenChange={() => setStatusChangeModal(null)}>
        <DialogContent className="sm:max-w-sm max-w-[95vw]"><DialogHeader><DialogTitle className="text-base">Status - {statusChangeModal?.user_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-1.5">
              {["interested", "not_interested", "follow_up", "not_connected"].map(r => (
                <Button key={r} variant={manualStatus.status === r ? "default" : "outline"} size="sm" onClick={() => setManualStatus({ ...manualStatus, status: r })} className="capitalize text-[10px] h-8 px-2">{r.replace(/_/g, " ")}</Button>
              ))}
            </div>
            <Textarea placeholder="Remarks..." value={manualStatus.notes} onChange={e => setManualStatus({ ...manualStatus, notes: e.target.value })} rows={2} className="text-sm" />
          </div>
          <DialogFooter className="flex-row gap-2"><Button variant="outline" size="sm" onClick={() => setStatusChangeModal(null)}>Cancel</Button><Button size="sm" onClick={submitManualStatus}>Update</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Modal with Meta Templates */}
      <Dialog open={!!whatsappModal} onOpenChange={() => setWhatsappModal(null)}>
        <DialogContent className="sm:max-w-md max-w-[95vw]"><DialogHeader><DialogTitle>WhatsApp - {whatsappModal?.user_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {waTemplates.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Approved Templates:</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {waTemplates.map((t, i) => (
                    <button key={i} className={`w-full text-left p-2 rounded border text-xs transition-colors ${whatsappMsg === t.body ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`} onClick={() => setWhatsappMsg(t.body)}>
                      <span className="font-medium">{t.name}</span>
                      <p className="text-muted-foreground mt-0.5 line-clamp-1">{t.body}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Textarea value={whatsappMsg} onChange={e => setWhatsappMsg(e.target.value)} rows={3} className="text-sm" placeholder="Type or select template..." />
          </div>
          <DialogFooter className="flex-row gap-2"><Button variant="outline" size="sm" onClick={() => setWhatsappModal(null)}>Cancel</Button><Button size="sm" onClick={sendWhatsApp}>Send</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Modal */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="sm:max-w-sm"><DialogHeader><DialogTitle>Assign {selectedClients.length} Clients</DialogTitle></DialogHeader>
          <Select value={assignAgent} onValueChange={setAssignAgent}><SelectTrigger><SelectValue placeholder="Select agent..." /></SelectTrigger><SelectContent>{agents.map(a => <SelectItem key={a._id} value={a._id}>{a.name}</SelectItem>)}</SelectContent></Select>
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setShowAssign(false)}>Cancel</Button><Button size="sm" onClick={handleAssign}>Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
        <DialogContent className="sm:max-w-sm"><DialogHeader><DialogTitle>Delete {selectedClients.length} Clients</DialogTitle></DialogHeader>
          <p className="text-sm text-destructive">History preserved. Enter admin password:</p>
          <Input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} className="h-9" />
          <DialogFooter><Button variant="outline" size="sm" onClick={() => { setDeleteModal(false); setDeletePassword(""); }}>Cancel</Button><Button variant="destructive" size="sm" onClick={handleDelete}>Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Edit All Client Details Modal */}
      {user?.role === "admin" && (
        <EditClientModal
          client={editClientModal}
          isOpen={!!editClientModal}
          onClose={() => setEditClientModal(null)}
          onSaveSuccess={() => fetchClients()}
          agents={agents}
        />
      )}
    </div>
  );
}
